import { AbcOutlined, CheckCircleRounded, CloseOutlined, DoNotDisturbOnRounded, DriveFileRenameOutlineOutlined, FilePresentOutlined, KeyOutlined, PinOutlined, RemoveCircleOutlineOutlined, SaveOutlined, Search, SearchOutlined } from "@mui/icons-material";
import { Box, Button, Chip, FormControl, Grid, IconButton, InputAdornment, InputLabel, Pagination, Paper, Skeleton, styled, TextField, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { IdentifierListType, IdentifierRequestType, IdentifierType, SelectedParametersType, UserType } from "../../../types/types";
import { userAtom } from "../../../atoms/atoms";
import { ContentProps, Toast } from "primereact/toast";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { getFilteredList, setToastRefToOpen } from "../../../functions/functions";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view";

import Rmodal from "../../../components/Rmodal";
import GridTemplate from "../../../components/GridTemplate";
import identifierFindAllCrudResponse from "../../../resources/Api/identifier/identifierFindAllCrudResponse";
import identifierOnSaveData from "../../../resources/Api/identifier/identifierOnSaveData";
import identifierOnUpdateData from "../../../resources/Api/identifier/identifierOnUpdateData";
import identifierUpdateStatusById from "../../../resources/Api/identifier/identifierUpdateStatusById";
import identifierFindAllExportResponse from "../../../resources/Api/identifier/identifierFindAllExportResponse";
import usePagination from "../../../utils/usePagination";

import * as xlsx from "xlsx";
import identifierOnSaveListData from "../../../resources/Api/identifier/identifierOnSaveListData";
import identifierFindAllAllResponse from "../../../resources/Api/identifier/identifierFindAllListResponse";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Ativo"}
                icon={<KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded
                            className="activated"
                            style={{ height: 28, width: 28 }}
                        />
                    ) : (
                        <DoNotDisturbOnRounded
                            className="deactivated"
                            style={{ height: 28, width: 28 }}
                        />
                    )
                }
            />
        ),
    },
    {
        field: "code",
        align: "left",
        width: 150,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Código"}
                icon={<PinOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.code}
            />
        ),
    },
    {
        field: "uniqueId",
        align: "left",
        width: 200,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Identificador"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.uniqueId}
            />
        ),
    },
    {
        field: "customerReducedName",
        align: "left",
        width: 150,
        editable: false,
        sortable: true,
        headerName: "Cliente",
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Cliente"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.customerReducedName ? item.row.customerReducedName : "Estoque"}
            />
        ),
    },
    {
        field: "departmentReduceName",
        align: "left",
        width: 200,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Departamento"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.departmentReduceName ? item.row.departmentReduceName : "-"}
            />
        ),
    },
    {
        field: "driverName",
        headerClassName: "last-column",
        align: "left",
        width: 150,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Motorista"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.driverName ? item.row.driverName : "-"}
            />
        ),
    }
];

const rowsOptions = (list: Array<IdentifierListType>): Array<IdentifierListType> => {
    const result = [...list];
    return result.map((item) => ({ ...item }));
};

/**
 * IdentifierPage - Component that renders a page to list, create and manage drivers identifiers as RFID or iButton
 * @component
 */
const IdentifierPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState(true);
    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    const [filteredList, setFilteredList] = useState<Array<IdentifierListType>>([]);
    const [query, setQuery] = useState<string>("");
    const [query2, setQuery2] = useState<string>("");
    const [page, setPage] = useState(1);

    const [identifierImportedList, setIdentifierImportedList] = useState<Array<IdentifierType>>([]);
    const [filteredImportedList, setFilteredImportedList] = useState<Array<IdentifierType>>([]);
    const [xlsxFile, setXlsxFile] = useState<string | null | undefined | File | any>(null);

    // states
    const [identifierList, setIdentifierList] = useState<Array<IdentifierListType>>([]);
    const [selectedIdentifier, setSelectedIdentifier] = useState<IdentifierListType>({
        id: -1,
        uniqueId: "",
        code: 0,
        customerReducedName: "",
        departmentReduceName: "",
        driverName: "",
        activated: true
    });

    const [code, setCode] = useState(0);
    const [uniqueId, setUniqueId] = useState("");
    const [code_1, setCode_1] = useState("");
    const [code_2, setCode_2] = useState("");
    const [code_3, setCode_3] = useState("");

    const [debouncedQuery, setDebouncedQuery] = useState(query);
    const [lastRemoteQuery, setLastRemoteQuery] = useState("");

    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    // refs
    const toastModalRef = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<any>(null);
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);

    // hooks e const
    const location = useLocation();

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setSelectedIdentifier({
            id: -1,
            uniqueId: "",
            code: 0,
            customerReducedName: "",
            departmentReduceName: "",
            driverName: "",
            activated: true
        });
        setUniqueId("");
        setCode(0);
        setInEdit(false);
        setInInsert(false);
        setIdentifierImportedList([]);
        setFilteredImportedList([]);
        setQuery("");
        setQuery2("");
        setCode_1("");
        setCode_2("");
        setCode_3("");
    }, []);

    const valueList = useMemo(() => filteredImportedList.length > 0 ? filteredImportedList : identifierImportedList, [filteredImportedList, identifierImportedList]);
    const count = Math.ceil(valueList.length / 40);
    const dataPage = usePagination<IdentifierType>({
        data: valueList,
        itemsPerPage: 40,
    });

    const handleChangePage = useCallback((event: any, page: any) => {
        setPage(page);
        dataPage.jump(page);
    }, [dataPage]);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    const getAllIdentifiersList = useCallback(
        async (search: string = "", status: string = "activated", page: number = 0, size: number = 100) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const currentParameters: SelectedParametersType = cookies.selectedParameters;
            setLoading(true);
            try {
                const { data } = await identifierFindAllCrudResponse(token, currentParameters.customer.id, status, page, size, search);
                setIdentifierList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);

                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setIdentifierList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [cookies.token, location?.state?.secretaryId, cookies.selectedParameters]
    );

    useEffect(() => {
        getAllIdentifiersList();
    }, [getAllIdentifiersList]);

    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);

        const data: IdentifierRequestType = {
            id: -1,
            uniqueId: uniqueId,
            managerId: currentParameters.customer.id,
            code: -1,
            activated: true
        };

        if (
            !data.uniqueId && identifierImportedList.length === 0
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            if (identifierImportedList.length > 0) {
                await identifierOnSaveListData(token, identifierImportedList);
            } else {
                await identifierOnSaveData(token, data);
            }
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllIdentifiersList();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        handleClose,
        identifierImportedList,
        getAllIdentifiersList,
        cookies.selectedParameters,
        cookies.token,
        uniqueId
    ]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;
        setLoading(true);

        const data: IdentifierRequestType = {
            id: selectedIdentifier.id,
            uniqueId,
            code,
            managerId: currentParameters.customer.id,
            activated: true
        };

        if (
            !data.uniqueId || !data.code
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            data.uniqueId === selectedIdentifier.uniqueId && data.code === selectedIdentifier.code
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await identifierOnUpdateData(token, data, data.id);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllIdentifiersList();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [cookies.token, cookies.selectedParameters, selectedIdentifier.id, selectedIdentifier.uniqueId, selectedIdentifier.code, uniqueId, code, handleClose, getAllIdentifiersList]);

    const verifyBeforeCancel = useCallback(
        (selected: IdentifierListType) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                if (
                    uniqueId !== selected.uniqueId ||
                    code !== selected.code
                ) {
                    canCancel = false;
                }
            } else if (
                !inEdit &&
                inInsert &&
                (uniqueId)
            ) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box
                                    className="flex flex-column align-items-left"
                                    style={{ flex: "1" }}
                                >
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">
                                        {props?.message.detail}
                                    </Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [
            inEdit,
            inInsert,
            handleClose,
            statusMessage,
            code,
            isToastVisible,
            uniqueId
        ]
    );

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query);
        }, 1000); // tempo para considerar que PAROU de digitar

        return () => clearTimeout(handler);
    }, [query]);

    const getBySearch = useCallback(() => {
            const localResult = getFilteredList(
                ["uniqueId", "code"],
                identifierList,
                debouncedQuery
            );

            if (localResult.length > 0) {
                setFilteredList(localResult);
            } else {
                setFilteredList([]);
            }

            // → evita loop: só chama API se query mudou
            if (debouncedQuery !== lastRemoteQuery && localResult.length <= 0) {
                setLastRemoteQuery(debouncedQuery);
                getAllIdentifiersList(debouncedQuery);
            }
    }, [
        debouncedQuery,
        getAllIdentifiersList,
        identifierList,
        lastRemoteQuery
    ]);

    useEffect(() => {
        getBySearch();
    }, [getBySearch]);

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                await identifierUpdateStatusById(token, id, status);
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllIdentifiersList();
                }, 8000);
            }
        },
        [cookies.token, getAllIdentifiersList, handleClose]
    );

    const calculateField1 = useCallback((uniqueId: string) => {
        // =EXT.TEXTO(C15354;3;12)&ESQUERDA(C15354;2)
        // Obtém uma subcadeia de texto começando do 3º caractere e com 12 caracteres de comprimento
        const subChain = uniqueId.substring(2, 14); // substring(2, 14) pega do índice 2 ao 13
        // Obtém os primeiros dois caracteres da string
        const firstTwoNumbers = uniqueId.substring(0, 2); // substring(0, 2) pega do índice 0 ao 1

        // Combina as duas subcadeias de texto
        const code_1 = subChain + firstTwoNumbers;
        return code_1;
    }, []);

    const calculateField2 = useCallback((uniqueId: string) => {
        // =+DIREITA(C15354;2)&DIREITA(ESQUERDA(C15354;14);12)&ESQUERDA(C15354;2)
        // Obtém os dois últimos caracteres da string
        const lastTwoNumbers = uniqueId.slice(-2);
        // Obtém os primeiros 14 caracteres e depois os 12 últimos caracteres dessa subcadeia
        const subChainLeft14 = uniqueId.substring(0, 14);
        const lastTenNumbersSubChain = subChainLeft14.slice(-12);
        // Obtém os primeiros dois caracteres da string
        const firstTwoNumber = uniqueId.substring(0, 2);

        // Combina as partes da string
        const response = lastTwoNumbers + lastTenNumbersSubChain + firstTwoNumber;
        return response;
    }, []);

    const calculateField3 = useCallback((uniqueId: string) => {
        // =EXT.TEXTO($E15354;13;2)&EXT.TEXTO($E15354;11;2)&EXT.TEXTO($E15354;9;2)&EXT.TEXTO($E15354;7;2)&EXT.TEXTO($E15354;5;2)&EXT.TEXTO($E15354;3;2)&EXT.TEXTO($E15354;1;2)
        const part1 = uniqueId.substring(0, 2);   // Characters at positions 1 and 2
        const part2 = uniqueId.substring(12, 14); // Characters at positions 13 and 14
        const part3 = uniqueId.substring(10, 12); // Characters at positions 11 and 12
        const part4 = uniqueId.substring(8, 10);  // Characters at positions 9 and 10
        const part5 = uniqueId.substring(6, 8);   // Characters at positions 7 and 8
        const part6 = uniqueId.substring(4, 6);   // Characters at positions 5 and 6
        const part7 = uniqueId.substring(2, 4);   // Characters at positions 3 and 4

        // Combina as substrings
        const code_3 = part1 + part2 + part3 + part4 + part5 + part6 + part7;
        return code_3;
    }, []);

    useEffect(() => {
        if (uniqueId && uniqueId.length >= 14 && uniqueId.length <= 16) {
            setCode_1(calculateField1(uniqueId));
            setCode_2(calculateField2(uniqueId));
            setCode_3(calculateField3(uniqueId));
        }
    }, [uniqueId, uniqueId.length, identifierList, calculateField1, calculateField2, calculateField3]);

    const readUploadFile = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;
        let idList = identifierList;

        if (xlsxFile) {
            setLoading(true);

            if (numberOfElements > 100) {
                try {
                    const { data } = await identifierFindAllAllResponse(token, currentParameters.customer.id, "all");
                    idList = data;
                } catch (error) {
                    setTextValidation("Erro ao validar a lista importada, recarrege e tente novamente.");
                    setStatusMessage("warn");
                    setFieldsIsValid(false);
                }
            }

            const reader = new FileReader();
            reader.readAsArrayBuffer(xlsxFile);
            reader.onloadend = (event: ProgressEvent<FileReader>): void => {
                const data = event.target?.result as ArrayBuffer;
                const workbook = xlsx.read(data, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = xlsx.utils.sheet_to_json(worksheet);

                const list: Array<IdentifierType> = [];
                if ((json[0] as any).__EMPTY_1 === "LISTAGEM GERAL DE IBUTTONS") {
                    json.forEach((item: any) => {
                        if (item?.__EMPTY_1 > 0 && item?.__EMPTY_2) {
                            const identificador: IdentifierType = {
                                id: -1,
                                code: item?.__EMPTY_1,
                                uniqueId: item?.__EMPTY_2,
                                managerId: currentParameters.customer.id,
                                code_1: item?.__EMPTY_4,
                                code_2: item?.__EMPTY_5,
                                code_3: item?.__EMPTY_6,
                                activated: true
                            };

                            if (idList.some((a) => a.uniqueId.toString().toLowerCase() === identificador.uniqueId.toString().toLowerCase())) {
                                return;
                            } else {
                                if (list.every((a) => a.uniqueId.toString().toLowerCase() !== identificador.uniqueId.toString().toLowerCase())) {
                                    list.push(identificador);
                                } else {
                                    return;
                                }
                            }
                        }
                    });
                } else {
                    setLoading(false);
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation("O arquivo importado não é válido, verifique e tente novamente.");
                };
                setIdentifierImportedList(list);
                setLoading(false);
            };
        };
    }, [xlsxFile, identifierList, cookies.selectedParameters, cookies.token, numberOfElements]);

    useEffect(() => {
        readUploadFile();
    }, [readUploadFile]);

    const getReport = useCallback(async (type: string) => {
        setLoading(true);
        const currentParameters: SelectedParametersType = cookies.selectedParameters;
        let dataBuffer = new ArrayBuffer(0);

        let reportType = type;
        let isSimpleType = false;
        if (type.includes("simple")) {
            reportType = reportType.split(" ")[0];
            isSimpleType = true;
        };
        // simpleXlsxExport = false

        try {
            const { data } = await identifierFindAllExportResponse(cookies.token,
                currentParameters.customer.id,
                (identifierList.length > 0 && identifierList[0].activated)
                    ? "activated"
                    : "deactivated", reportType, isSimpleType);

            if ((data as ArrayBuffer).byteLength === 0) {
                setTextValidation("Nenhum dado para mostrar no relatório.");
                setStatusMessage("warn");
                setFieldsIsValid(false);
            } else {
                setTextValidation("Relatório gerado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
                dataBuffer = data;
            }
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                setReport(dataBuffer);
                setExportType(type);
            }, 3000);
        }
    }, [cookies.token, identifierList, cookies.selectedParameters, handleClose]);

    useEffect(() => {
        if (report.byteLength > 0) {
            const typeExport = (exportType === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const handleOnEditValues = useCallback(
        async (selected: IdentifierType) => {
            if (selected?.uniqueId && !inInsert) {
                setUniqueId(selected.uniqueId);
                setCode(selected.code);
            }
        },
        [inInsert]
    );

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid
                className="container "
                sx={{
                    height: 350,
                    width: 950,
                }}
            >
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 350, padding: 5 }}>
                        <Skeleton height={80} />
                        <Skeleton height={80} />
                        <Skeleton height={80} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const CustomTreeItem = styled(TreeItem)({
        [`& .${treeItemClasses.iconContainer}`]: {
            "& .close": {
                opacity: 0.3,
            },
        },
    });

    useEffect(() => {
        setFilteredList(
            getFilteredList(
                ["uniqueId", "code"],
                identifierList,
                query
            )
        );
    }, [identifierList, query]);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);

                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                    sx={style}
                >
                    <Box
                        className="row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                            "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                        }}
                    >
                        {!loading ? (
                            <>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Identificação
                                    </Typography>
                                </Box>
                                <Box
                                    className="col-sm-12 col-md-5"
                                    style={{ display: "flex", justifyContent: "end" }}
                                >
                                    {selectedIdentifier?.uniqueId && (
                                        <>
                                            <Chip
                                                style={{
                                                    color: selectedIdentifier.activated
                                                        ? "#00B700"
                                                        : "#dc3545",
                                                    marginRight: 5,
                                                    width: "50%",
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => { }}
                                                label={selectedIdentifier.activated ? "Ativo" : "Inativo"}
                                            />

                                            <Chip
                                                style={{
                                                    color: "black",
                                                    marginRight: 5,
                                                }}
                                                sx={{
                                                    "& .MuiChip-label": {
                                                        minWidth: 100,
                                                        width: `${String(code).length + 10}ch`,
                                                        display: "flex",
                                                        alignItems: "center"
                                                    },
                                                }}
                                                clickable={false}
                                                onClick={() => { }}
                                                label={
                                                    (selectedIdentifier?.code &&
                                                        !inEdit &&
                                                        !inInsert) as boolean 
                                                        ? `Cód: ${selectedIdentifier.code}`
                                                        : <TextField
                                                            variant="standard"
                                                               InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="end" style={{ marginRight: "2px" }}>
                                                                            Cód:
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            value={code}
                                                            onChange={(event) => {
                                                                setCode(Number(event.target.value));
                                                            }}
                                                            type='unknown'
                                                            prefix='Cód: '
                                                            sx={{
                                                                "& input": {
                                                                    textAlign: "center",
                                                                    padding: 0,
                                                                    fontSize: 14,
                                                                },
                                                            }}
                                                        />
                                                    }
                                            />

                                            {selectedIdentifier.activated && (
                                                <Chip
                                                    style={{
                                                        color: "black",
                                                        marginRight: 5,
                                                        width: "50%",
                                                    }}
                                                    disabled={!location?.state?.canEdit}
                                                    onClick={() => {
                                                        setInEdit(true);
                                                    }}
                                                    label="Editar"
                                                    icon={
                                                        <DriveFileRenameOutlineOutlined
                                                            style={{ height: 24, width: 24, fill: "black" }}
                                                        />
                                                    }
                                                />
                                            )}
                                            <Chip
                                                style={{
                                                    color: "black",
                                                    marginRight: 5,
                                                    width: "50%",
                                                }}
                                                disabled={!location?.state?.canEdit}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeUpdateStatus(
                                                            selectedIdentifier.id,
                                                            selectedIdentifier.activated
                                                                ? "deactivate"
                                                                : "activate"
                                                        );
                                                    }
                                                }}
                                                label={
                                                    selectedIdentifier.activated ? "Desativar" : "Ativar"
                                                }
                                                icon={
                                                    <RemoveCircleOutlineOutlined
                                                        style={{ height: 24, width: 24, fill: "black" }}
                                                    />
                                                }
                                            />
                                        </>
                                    )}
                                    {inInsert && <>
                                        <input
                                            type="file"
                                            style={{ display: "none" }}
                                            ref={fileInputRef}
                                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                const target = event.target;
                                                const file = target?.files;

                                                if (file && file[0]) {
                                                    setXlsxFile(file[0]);
                                                } else {
                                                    setXlsxFile(null);
                                                }
                                            }}
                                        />
                                        <Chip
                                            style={{
                                                color: "black",
                                                marginRight: 5,
                                                width: "50%",
                                            }}
                                            disabled={!location?.state?.canEdit}
                                            onClick={(e: any) => {
                                                e.preventDefault();
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.click();
                                                }
                                            }}
                                            label="Importar"
                                            icon={
                                                <FilePresentOutlined
                                                    style={{ height: 24, width: 24, fill: "black" }}
                                                />
                                            }
                                        /></>}
                                </Box>

                                {identifierImportedList.length === 0 ? <>
                                    <Box
                                        className="col-md-12"
                                        style={{ margin: "15px 0px 15px 0px" }}
                                    >
                                        <FormControl
                                            className="form-group"
                                            style={{ display: "flex", width: "100%" }}
                                        >
                                            <InputLabel
                                                id="demo-multiple-password-label"
                                                shrink
                                                disabled={
                                                    (selectedIdentifier?.uniqueId &&
                                                        !inEdit &&
                                                        !inInsert) as boolean
                                                }
                                                style={{ fontFamily: "Prometo" }}
                                                required
                                            >
                                                Indentificador
                                            </InputLabel>
                                            <TextField
                                                name="name"
                                                autoComplete="off"
                                                InputProps={{
                                                    label: "Indentificador",
                                                    notched: true,
                                                    style: { fontFamily: "Prometo" },
                                                }}
                                                disabled={
                                                    (selectedIdentifier?.uniqueId &&
                                                        !inEdit &&
                                                        !inInsert) as boolean
                                                }
                                                value={uniqueId}
                                                onChange={(event) => {
                                                    setUniqueId(event.target.value);
                                                }}
                                                type="text"
                                                required
                                                className="form-control form-control-login"
                                                placeholder="Digite o número do identificador (IButton/RFID)"
                                            />
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                className="visibility-password"
                                                disabled
                                                onMouseDown={(e: SyntheticEvent) => { }}
                                                edge="end"
                                                size="large"
                                            >
                                                <PinOutlined />
                                            </IconButton>
                                        </FormControl>
                                    </Box>
                                    {uniqueId && <Box className="col-md-12"
                                        style={{ display: "flex", width: "100%" }}>
                                        {[{ name: "Código 1", id: code_1, key: "c1" }, { name: "Código 2", id: code_2, key: "c2" }, { name: "código 3", id: code_3, key: "c3" }].map((cod) => {
                                            return (
                                                <Chip
                                                    key={cod.key}
                                                    label={`${cod.name}: ${cod.id}`}
                                                    style={{ marginRight: 1, cursor: "pointer" }}
                                                />
                                            )
                                        })}
                                    </Box>}
                                </> : <>
                                    <Box
                                        className="col-md-12"
                                        style={{ margin: "15px 0px 20px 0px" }}
                                    >
                                        <FormControl
                                            className="form-group"
                                            style={{ display: "flex" }}
                                        >
                                            <TextField
                                                id="chip-label-select-group"
                                                className="form-control input-select-group"
                                                autoComplete="off"
                                                ref={(ref) => autocompleteRef.current = ref}
                                                type="text"
                                                placeholder="Pesquise por número ou código"
                                                style={{
                                                    width: "100%",
                                                    minHeight: "56px",
                                                    height: "56px",
                                                    backgroundColor: "#ffffff29",
                                                    borderColor: "lightgray",
                                                }}
                                                label="Identificador"
                                                value={query2}
                                                onChange={(event) => setQuery2(event.target.value)}
                                                onKeyDown={(event) => {
                                                    return (event.key === "Enter" ? setFilteredImportedList(
                                                        getFilteredList(
                                                            ["uniqueId", "code"],
                                                            identifierImportedList,
                                                            query2
                                                        )
                                                    ) : null)
                                                }}
                                            />
                                            <IconButton
                                                className="visibility-password"
                                                disabled
                                                onMouseDown={(e: SyntheticEvent) => { }}
                                                edge="end"
                                                size="large"
                                            >
                                                <SearchOutlined />
                                            </IconButton>
                                        </FormControl>
                                    </Box>
                                    <Box
                                        style={{
                                            display: "grid",
                                            padding: "20px",
                                            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                                            gap: "10px",
                                        }}
                                    >
                                        {!loading && dataPage.currentData().map((it, idx) => {
                                            return (
                                                <Box key={idx}>
                                                    <Tooltip
                                                        title={
                                                            <Box>
                                                                <Typography>Cód: {it.code}</Typography>
                                                                <Typography>Identificador: {it.uniqueId}</Typography>
                                                                <Typography>Código 01: {it.code_1}</Typography>
                                                                <Typography>Código 02: {it.code_2}</Typography>
                                                                <Typography>Código 03: {it.code_3}</Typography>

                                                            </Box>
                                                        }
                                                        style={{ color: "black", margin: 5, cursor: "pointer" }}
                                                    >
                                                        <Chip
                                                            label={it.uniqueId ?? it.code_1 ?? it.code_2 ?? it.code_3}
                                                            onDelete={() => {
                                                                const newList = identifierImportedList.filter(
                                                                    (item) => item.code !== it.code
                                                                );
                                                                setFilteredImportedList([]);
                                                                setQuery2("");
                                                                setIdentifierImportedList(newList);
                                                            }}
                                                            style={{ margin: 1, cursor: "pointer" }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </>
                                }
                                {identifierImportedList.length > 0 && <Pagination
                                    style={{
                                        color: "#ffff",
                                        fontFamily: "Prometo",
                                        display: "flex",
                                        justifyContent: "end",
                                    }}
                                    count={count}
                                    size="large"
                                    page={page}
                                    shape="circular"
                                    onChange={handleChangePage}
                                    showFirstButton
                                    showLastButton
                                />}
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", width: "100%", alignSelf: "stretch", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0, 0, 0, 0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedIdentifier);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => {
                                    inInsert && !inEdit ? onSave() : onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [query2, dataPage, selectedIdentifier, location?.state?.canEdit, isToastVisible, validationText, isOpenDetails, loading, loadingEsqueleton, inInsert, inEdit, uniqueId, onSave, onUpdate, verifyBeforeUpdateStatus, verifyBeforeCancel, identifierImportedList, handleChangePage, page, count, code, code_1, code_2, code_3]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName="Identificadores"
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList)}
                sortField="code"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"IButton/RFID ou código"}
                onRefresh={getAllIdentifiersList}
                onExport={(type: string) => getReport(type)}
                disableAddButton={!location?.state?.canCreate}
                handleNext={(activated: string, page: number, size: number) => getAllIdentifiersList("", activated, page, size)}
                handlePrevious={() => { }}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedIdentifier(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(94vw, 950px)" },
    maxWidth: 950,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default IdentifierPage;
